import React, { useEffect, useMemo, useRef } from "react";
import { DateHelper } from "../../../../helpers/DateHelpers";
import { loadFromStorage, removeFromStorage, saveToStorage } from "../../../../helpers/StorageHelpers";
import { LocalizeContextProps, TranslateFunction, withLocalize } from "react-localize-redux";
import { DateSelectionOutput, DateSelectionSection } from "./DateSelectionSection";
import { StorageWrapper } from "@src/services/StorageWrapper";
import { LocalStorageKeys } from "../../../../models/StorageKeys";
import { NewformaDropdownOption } from "@src/components/_base/NewformaDropdown";

const ON_DATE_KEY = "on-date";

interface ReminderDropdownOption extends NewformaDropdownOption {
    days: number;
    isBusiness: boolean;
    isBefore: boolean;
}

function buildReminderOptions(
    expirationDate: Date | undefined,
    isTransferChecked: boolean,
    translate: TranslateFunction
): ReminderDropdownOption[] {
    const options: ReminderDropdownOption[] = [];
    const t_in = translate("SHARED.MOREOPTIONS.REMINDER_DROPDOWN.IN") as string;
    const t_business = translate("SHARED.MOREOPTIONS.REMINDER_DROPDOWN.BUSINESS") as string;
    const t_day = translate("SHARED.MOREOPTIONS.REMINDER_DROPDOWN.DAY") as string;
    const t_days = translate("SHARED.MOREOPTIONS.REMINDER_DROPDOWN.DAYS") as string;
    const t_beforeExp = translate("SHARED.MOREOPTIONS.REMINDER_DROPDOWN.BEFORE_EXPIRATION") as string;

    // "In X business days before expiration"
    for (let i = 1; i <= 10; i++) {
        const opt: ReminderDropdownOption = {
            key: `bd-before-${i}`,
            text: `${t_in} ${i} ${t_business} ${i === 1 ? t_day : t_days} ${t_beforeExp}`,
            days: i,
            isBusiness: true,
            isBefore: true,
        };
        if (isReminderOptionValid(opt, expirationDate, isTransferChecked)) {
            options.push(opt);
        }
    }

    // "In X business days" from today
    for (let i = 1; i <= 10; i++) {
        const opt: ReminderDropdownOption = {
            key: `bd-after-${i}`,
            text: `${t_in} ${i} ${t_business} ${i === 1 ? t_day : t_days}`,
            days: i,
            isBusiness: true,
            isBefore: false,
        };
        if (isReminderOptionValid(opt, expirationDate, isTransferChecked)) {
            options.push(opt);
        }
    }

    // Weeks & month
    const fixedOptions: ReminderDropdownOption[] = [
        {
            key: "r-7",
            text: translate("SHARED.MOREOPTIONS.REMINDER_DROPDOWN.1_WEEK") as string,
            days: 7,
            isBusiness: false,
            isBefore: false,
        },
        {
            key: "r-14",
            text: translate("SHARED.MOREOPTIONS.REMINDER_DROPDOWN.2_WEEKS") as string,
            days: 14,
            isBusiness: false,
            isBefore: false,
        },
        {
            key: "r-21",
            text: translate("SHARED.MOREOPTIONS.REMINDER_DROPDOWN.3_WEEKS") as string,
            days: 21,
            isBusiness: false,
            isBefore: false,
        },
        {
            key: "r-28",
            text: translate("SHARED.MOREOPTIONS.REMINDER_DROPDOWN.1_MONTH") as string,
            days: 28,
            isBusiness: false,
            isBefore: false,
        },
    ];
    for (const opt of fixedOptions) {
        if (isReminderOptionValid(opt, expirationDate, isTransferChecked)) {
            options.push(opt);
        }
    }

    // On Date
    options.push({
        key: ON_DATE_KEY,
        text: translate("SHARED.MOREOPTIONS.REMINDER_DROPDOWN.ON_DATE") as string,
        days: 0,
        isBusiness: false,
        isBefore: false,
    });

    return options;
}

function isReminderOptionValid(
    opt: ReminderDropdownOption,
    expirationDate: Date | undefined,
    isTransferChecked: boolean
): boolean {
    const today = DateHelper.TodayDate;

    if (!expirationDate || !isTransferChecked) return !opt.isBefore;

    if (opt.isBefore) {
        const calcDate = DateHelper.addDays(expirationDate, -opt.days, opt.isBusiness);
        const diff = DateHelper.subDate(today, calcDate);

        return diff > 0;
    } else {
        const calcDate = DateHelper.addDays(today, opt.days, opt.isBusiness);
        const diff = DateHelper.subDate(calcDate, expirationDate);

        return diff > 0;
    }
}

function computeReminderDate(opt: ReminderDropdownOption, expirationDate: Date | undefined): Date {
    const today = DateHelper.TodayDate;
    if (opt.isBefore && expirationDate) {
        return DateHelper.addDays(expirationDate, -opt.days, opt.isBusiness);
    }
    const days = opt.days === 0 ? 1 : opt.days;
    return DateHelper.addDays(today, days, opt.isBusiness);
}

function fallbackToOneDayBefore(
    reminderOptions: ReminderDropdownOption[],
    expirationDate: Date | undefined,
    onChange?: (output: DateSelectionOutput) => void
): void {
    const oneDayBefore = reminderOptions.find((o) => o.isBefore && o.days === 1);
    if (oneDayBefore) {
        onChange?.({
            Checked: true,
            SelectedOption: oneDayBefore.key,
            SelectedDate: computeReminderDate(oneDayBefore, expirationDate),
        });
    } else {
        onChange?.({
            Checked: false,
            SelectedOption: "",
            SelectedDate: undefined,
        });
    }
}

export interface SendRemindersSectionProps extends LocalizeContextProps {
    dateSelection: DateSelectionOutput;
    isExpiredChecked: boolean;
    expirationDate: Date | undefined;
    storageWrapper: StorageWrapper;
    isInactive: boolean;
    onDateSelectionChange?: (output: DateSelectionOutput) => void;
}

export const SendRemindersSection = withLocalize((props: SendRemindersSectionProps) => {
    const [savedChecked, setSavedChecked] = React.useState<boolean>(false);
    const [savedSelectedOption, setSavedSelectedOption] = React.useState<string>("");
    const [savedDays, setSavedDays] = React.useState<number>(0);

    const isChangedRef = useRef(false);

    const reminderOptions = useMemo(
        () => buildReminderOptions(props.expirationDate, props.isExpiredChecked, props.translate),
        [props.expirationDate, props.isExpiredChecked, props.translate]
    );

    const expirationIsTodayOrTomorrow = useMemo(() => {
        if (!props.isExpiredChecked || !props.expirationDate) return false;

        const today = DateHelper.TodayDate;
        const tomorrow = DateHelper.addDays(today, 1);

        return (
            DateHelper.isSameDay(props.expirationDate, today) || DateHelper.isSameDay(props.expirationDate, tomorrow)
        );
    }, [props.isExpiredChecked, props.expirationDate]);

    useEffect(() => {
        if (!props.dateSelection.Checked || !props.dateSelection.SelectedOption) return;

        if (expirationIsTodayOrTomorrow) {
            props.onDateSelectionChange?.({
                Checked: false,
                SelectedOption: props.dateSelection.SelectedOption,
                SelectedDate: props.dateSelection.SelectedDate,
            });
            return;
        }

        if (props.dateSelection.SelectedOption === ON_DATE_KEY) {
            if (
                props.isExpiredChecked &&
                props.dateSelection.SelectedDate &&
                props.expirationDate &&
                props.dateSelection.SelectedDate >= props.expirationDate
            ) {
                fallbackToOneDayBefore(reminderOptions, props.expirationDate, props.onDateSelectionChange);
            }
            return;
        }

        const stillValid = reminderOptions.some((o) => o.key === props.dateSelection.SelectedOption);
        if (!stillValid) {
            const beforeFallback = reminderOptions.find((o) => o.isBefore && o.days === 1);
            const fallbackOption = beforeFallback ?? reminderOptions.find((o) => o.key === "bd-after-1");
            if (fallbackOption) {
                props.onDateSelectionChange?.({
                    Checked: true,
                    SelectedOption: fallbackOption.key,
                    SelectedDate: computeReminderDate(fallbackOption, props.expirationDate),
                });
            } else {
                props.onDateSelectionChange?.({
                    Checked: false,
                    SelectedOption: "",
                    SelectedDate: undefined,
                });
            }
        }
    }, [reminderOptions, expirationIsTodayOrTomorrow]);

    useEffect(() => {
        const saved = loadFromStorage(props.storageWrapper, LocalStorageKeys.reminderDate);
        if (saved) {
            setSavedChecked(true);
            setSavedSelectedOption(saved.key);
            setSavedDays(saved.days ?? 0);
        } else {
            setSavedChecked(false);
            setSavedSelectedOption("");
            setSavedDays(0);
        }
    }, [props.isInactive]);

    useEffect(() => {
        if (props.isInactive) {
            isChangedRef.current = false;
            return;
        }
        if (isChangedRef.current) return;
        if (!savedChecked) return;

        const option = reminderOptions.find((o) => o.key === savedSelectedOption);
        if (option) {
            const selectedDate =
                savedSelectedOption === ON_DATE_KEY && savedDays > 0
                    ? DateHelper.addDays(DateHelper.TodayDate, savedDays)
                    : computeReminderDate(option, props.expirationDate);

            if (props.isExpiredChecked && props.expirationDate && selectedDate >= props.expirationDate) {
                fallbackToOneDayBefore(reminderOptions, props.expirationDate, props.onDateSelectionChange);
                return;
            }

            props.onDateSelectionChange?.({
                Checked: true,
                SelectedOption: savedSelectedOption,
                SelectedDate: selectedDate,
            });
        }
    }, [props.isInactive, savedChecked, savedSelectedOption, reminderOptions, props.expirationDate]);

    const handleDateSelectionChange = (output: DateSelectionOutput) => {
        const outputToSave = { ...output };

        if (output.Checked) {
            if (!output.SelectedOption) {
                const defaultKey = props.isExpiredChecked ? "bd-before-1" : "bd-after-1";
                const defaultOption = reminderOptions.find((o) => o.key === defaultKey) ?? reminderOptions[0];
                if (defaultOption) {
                    outputToSave.SelectedOption = defaultOption.key;
                    outputToSave.SelectedDate = computeReminderDate(defaultOption, props.expirationDate);
                }
            } else if (output.SelectedOption === ON_DATE_KEY) {
                if (!output.SelectedDate) {
                    outputToSave.SelectedDate = DateHelper.addDays(DateHelper.TodayDate, 1);
                } else if (
                    props.isExpiredChecked &&
                    props.expirationDate &&
                    output.SelectedDate >= props.expirationDate
                ) {
                    fallbackToOneDayBefore(reminderOptions, props.expirationDate, props.onDateSelectionChange);
                    return;
                }
            } else {
                const opt = reminderOptions.find((o) => o.key === output.SelectedOption);
                if (opt) outputToSave.SelectedDate = computeReminderDate(opt, props.expirationDate);
            }

            const option = reminderOptions.find((o) => o.key === outputToSave.SelectedOption);
            if (option) {
                const days =
                    outputToSave.SelectedOption === ON_DATE_KEY && outputToSave.SelectedDate
                        ? DateHelper.subDate(DateHelper.TodayDate, outputToSave.SelectedDate)
                        : option.days;

                saveToStorage(props.storageWrapper, LocalStorageKeys.reminderDate, {
                    key: option.key,
                    text: option.text,
                    days,
                });
            }
        } else removeFromStorage(props.storageWrapper, LocalStorageKeys.reminderDate);

        isChangedRef.current = true;
        props.onDateSelectionChange?.(outputToSave);
    };

    const reminderMaxDate = useMemo(() => {
        if (!props.isExpiredChecked || !props.expirationDate) return undefined;
        return DateHelper.addDays(props.expirationDate, -1);
    }, [props.isExpiredChecked, props.expirationDate]);

    return (
        <>
            <DateSelectionSection
                testId="send-reminders-section"
                label={props.translate("SHARED.MOREOPTIONS.CHECKBOX_REMINDER") as string}
                options={reminderOptions}
                selectedOption={props.dateSelection.SelectedOption}
                optionsPlaceholder={props.translate("SHARED.MOREOPTIONS.DROPDOWN_PLACEHOLDER_REMINDER") as string}
                checked={props.dateSelection.Checked}
                selectedDate={props.dateSelection.SelectedDate}
                disabled={expirationIsTodayOrTomorrow}
                maxDate={reminderMaxDate}
                onChange={handleDateSelectionChange}
            />
        </>
    );
});
